import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, Calendar, Building2 } from 'lucide-react';
import { format } from 'date-fns';
import { currentDateFnsLocale } from '@/i18n/locale';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { callDate, callOutcome } from './sections/portalFigures';

/**
 * The portal home's « Derniers appels » table. W5 (DES-20): no staggered row
 * reveal, no decorative gradient avatar; the play button has a name.
 * QA-W5-5: the date and the result are read with portalFigures, like the
 * Calls tab, so the dialer preview's raw ParallelCalls (`started_at`,
 * `call_outcome`) show the same as the portal's merged rows.
 */

/** view-specific colours only; the label always comes from outcomeLabel() (ADR-032). */
const outcomeColor: Record<string, string> = {
    meeting_booked: 'bg-green-100 text-green-700',
    callback_scheduled: 'bg-blue-100 text-blue-700',
    not_interested: 'bg-gray-100 text-gray-600',
    no_answer: 'bg-yellow-100 text-yellow-700',
    wrong_number: 'bg-red-100 text-red-600',
    gatekeeper: 'bg-orange-100 text-orange-700',
};

interface CallRow {
    id?: string;
    prospect_name?: string | null;
    prospect_title?: string | null;
    prospect_company?: string | null;
    call_date?: string | Date | null;
    started_at?: string | Date | null;
    created_date?: string | Date | null;
    duration_seconds?: number | null;
    outcome?: string | null;
    call_outcome?: string | null;
    call_status?: string | null;
    recording_url?: string | null;
    meeting_date?: string | Date | null;
}

interface CallsTableProps {
    calls?: CallRow[];
    onPlayRecording: (url: string) => void;
}

export default function CallsTable({ calls, onPlayRecording }: CallsTableProps) {
    const { t } = useTranslation(['admin', 'dialer']);
    if (!calls || calls.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                {t('admin:backoffice.calls.noCalls')}
            </div>
        );
    }

    return (
        <div className="relative overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">{t('admin:backoffice.calls.detail.prospect')}</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">{t('common:field.company')}</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">{t('common:field.date')}</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">{t('admin:backoffice.calls.columns.duration')}</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">{t('admin:backoffice.calls.outcomePlaceholder')}</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">{t('admin:backoffice.prospects.columns.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {calls.map((call) => {
                        const date = callDate(call);
                        const outcome = callOutcome(call) || call.call_status || '';
                        return (
                        <tr key={call.id} className="border-b border-gray-50">
                            <td className="py-2 px-3">
                                <p className="text-sm font-medium text-gray-900">{call.prospect_name}</p>
                                {call.prospect_title && <p className="text-xs text-gray-500">{call.prospect_title}</p>}
                            </td>
                            <td className="py-2 px-3">
                                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <Building2 className="w-3 h-3" aria-hidden />
                                    {call.prospect_company}
                                </div>
                            </td>
                            <td className="py-2 px-3 text-xs text-gray-600">
                                {date && format(date, 'dd MMM yyyy, HH:mm', { locale: currentDateFnsLocale() })}
                            </td>
                            <td className="py-2 px-3 text-xs text-gray-600">
                                {call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}` : '-'}
                            </td>
                            <td className="py-2 px-3">
                                <Badge className={((outcome && outcomeColor[outcome]) || 'bg-gray-100') + ' text-xs px-2 py-0.5'}>
                                    {outcome ? outcomeLabel(outcome, t) : '—'}
                                </Badge>
                            </td>
                            <td className="py-2 px-3">
                                <div className="flex items-center gap-2">
                                    {call.recording_url && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => onPlayRecording(call.recording_url!)}
                                            aria-label={t('admin:backoffice.calls.detail.playInPlayer')}
                                            title={t('admin:backoffice.calls.detail.playInPlayer')}
                                            className="h-11 w-11 p-0 text-gray-700"
                                        >
                                            <Play className="w-4 h-4" aria-hidden />
                                        </Button>
                                    )}
                                    {call.meeting_date && (
                                        <div className="flex items-center gap-1 text-xs text-green-600">
                                            <Calendar className="w-3 h-3" />
                                            {!isNaN(new Date(call.meeting_date).getTime()) ? format(new Date(call.meeting_date), 'dd/MM') : '—'}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}